// rimPillStack.js — THE rim-pill stack layout, shared by both wheel engines
// (D3Wheels/createPie.js = Edit Wheel / Builder, renderer-v2/engine.js = View
// Wheel). stgfix-10120830-rimpills-kelly (T4.18 / T22.129 / T22.130, Kelly +
// Gordon 2026-10-12, wheel 507342, 33 segments): the two engines each carried
// their own copy of the Pill Rule A pass and the copies had drifted —
//   - Edit Wheel ran the residual X pass AFTER restoring the pill transitions,
//     so every relayout animated pills through an overlap for 220ms;
//   - View Wheel never bounded the stack by the room it really has and never
//     ran the residual X pass at all.
// Both now call layoutRimPillStack, so the pills land in the SAME place on
// both pages. Engine-specific transform writing stays in each engine's
// callbacks (moveY / moveRadial / moveX); the measuring, the stack, the ring
// re-check and the residual pass live only here.

import {
  keepPolePillsOutermost,
  resolveCircularPillFan,
  resolveClockwiseTangentOrder,
  resolveTightPillStack,
  resolveResidualPillOverlaps,
  resolveRoomlessPillCascade,
  resolveSideColumnFit,
  runRingSafetyCorrectionLoop,
} from "./labelLayout";
import { findClipBoundaryRect } from "./labelRadialX";
import { isDenseRimPillCrowd, resolveDenseRimPillColumns } from "./denseRimPillColumns";

// The room the pills really have, in page px. Top = the clipping ancestor (the
// canvas pane); bottom = the Builder's fit area when the wheel sits in one
// (review-1008-Frontend-6764), else the wheel's own <svg> box; both inside the
// window. minX/maxX bound the residual X pass the same way. `vertical:
// false` (View Wheel) leaves the stack vertically unbounded, as it always was
// there: the page scrolls and the pills paint past the <svg> (overflow
// visible).
export function rimPillStackRoom(svgEl, parentEl, win, { vertical = true } = {}) {
  let root = svgEl || null;
  while (root && root.ownerSVGElement) root = root.ownerSVGElement;
  const clip = parentEl ? findClipBoundaryRect(parentEl) : null;
  const hasClip = !!(clip && clip.height > 0);
  const roomEl =
    (root && typeof root.closest === "function"
      ? root.closest('[data-testid="builder-wheel-fit-area"]')
      : null) || root;
  const roomRect = roomEl ? roomEl.getBoundingClientRect() : null;
  // stgfix-10130814-T22131: controls painted OVER the canvas (the Builder's
  // Undo/Redo cluster, marked data-rim-pill-obstacle) — the room fallback in
  // layoutRimPillStack never shifts a pill under one.
  const doc = (root && root.ownerDocument) || (parentEl && parentEl.ownerDocument) || null;
  const obstacles = doc
    ? Array.from(doc.querySelectorAll("[data-rim-pill-obstacle]"))
        .map((el) => el.getBoundingClientRect())
        .filter((r) => r && r.width > 0 && r.height > 0)
        .map((r) => ({ left: r.left, right: r.right, top: r.top, bottom: r.bottom }))
    : [];
  const winH = win && win.innerHeight > 0 ? win.innerHeight : Infinity;
  const winW = win && win.innerWidth > 0 ? win.innerWidth : Infinity;
  // T22.432 (Gordon 2026-10-19, prod /builder/896121?pane=4, ~905px window;
  // MEASURED web-stg main.dcbe2e54 /builder/509802 @905x530, 29 segments):
  // the fit area ends 28-44px ABOVE the fixed bottom actions bar — the empty
  // gap is T22.131's in-flow reserve spacer, kept only to centre the wheel.
  // Bounding the stack at the fit area left the 6 o'clock crowd one row short,
  // so the room fallback slid 13 of 29 pills up to 34deg round the rim. The
  // real floor is the bar itself (data-rim-pill-floor); it only ever RELAXES
  // the fit-area bound, never moves a pill under the bar.
  let roomBottom = roomRect && roomRect.height > 0 ? roomRect.bottom : Infinity;
  if (roomEl && roomEl !== root && Number.isFinite(roomBottom)) {
    const floorTop = rimPillFloorTop(doc);
    if (floorTop > roomBottom) roomBottom = floorTop;
  }
  return {
    minTop: !vertical ? -Infinity : Math.max(0, hasClip ? clip.top : 0),
    maxBottom: !vertical ? Infinity : Math.min(
      winH,
      hasClip ? clip.bottom : Infinity,
      roomBottom
    ),
    minX: clip ? Math.max(0, clip.left) : 0,
    maxX: clip ? Math.min(winW, clip.right) : winW,
    obstacles,
  };
}

// Top edge (page px) of the controls marked data-rim-pill-floor — the box
// itself AND its visible children (a child can poke above its bar). Infinity
// when none is rendered.
function rimPillFloorTop(doc) {
  if (!doc) return Infinity;
  let top = Infinity;
  for (const el of Array.from(doc.querySelectorAll("[data-rim-pill-floor]"))) {
    for (const node of [el, ...Array.from(el.children || [])]) {
      const r = node.getBoundingClientRect();
      if (r && r.width > 0 && r.height > 0) top = Math.min(top, r.top);
    }
  }
  return top;
}

function hasPillOverlap(boxes) {
  for (let i = 0; i < boxes.length; i++) {
    for (let j = i + 1; j < boxes.length; j++) {
      const a = boxes[i];
      const b = boxes[j];
      const ox = Math.min(a.right, b.right) - Math.max(a.left, b.left);
      const oy = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
      if (ox > 0.5 && oy > 0.5) return true;
    }
  }
  return false;
}

// T22.432: how far (radians) the point of a pill nearest the wheel sits from
// its own wedge's mid-angle — "is the pill next to its segment".
function pillWedgeOffset(box, midAngle, center) {
  const nx = Math.max(box.left, Math.min(center.x, box.right));
  const ny = Math.max(box.top, Math.min(center.y, box.bottom));
  const a = Math.atan2(nx - center.x, -(ny - center.y));
  const d = Math.abs((((a - midAngle) % (2 * Math.PI)) + 3 * Math.PI) % (2 * Math.PI) - Math.PI);
  return d;
}

// T22.432 (Gordon 2026-10-19, prod /builder/896121?pane=4): in a Builder
// canvas too short for the crowd (a bounded room), the passes above can leave
// pills a quarter-turn from their wedges — MEASURED web-stg main.dcbe2e54,
// /builder/509803 @905x530 (33 segments): 24 of 33 pills more than one wedge
// away, worst 38deg. When some pill ends up more than one wedge from its own
// segment, lay the crowd out as two side columns instead
// (labelLayout.js resolveSideColumnFit) and keep that only if it brings the
// worst pill closer. A layout where every pill is already next to its wedge
// is never touched; the View Wheel (unbounded room) never gets here.
function applySideColumnFallback(rects, naturalBoxes, { room, ring, requiredGapPx }) {
  if (!ring || !ring.center || !(Number.isFinite(room.minTop) && Number.isFinite(room.maxBottom))) return;
  if (naturalBoxes.length < 2) return;
  const wedge = (2 * Math.PI) / naturalBoxes.length;
  const now = new Map();
  let worstNow = 0;
  for (const { p } of rects) {
    const r = p.measureEl.getBoundingClientRect();
    if (!(r.width > 0 && r.height > 0)) return;
    now.set(p, r);
    worstNow = Math.max(worstNow, pillWedgeOffset(r, p.midAngle, ring.center));
  }
  if (worstNow <= wedge) return;
  const fit = resolveSideColumnFit(naturalBoxes, {
    center: ring.center,
    radius: ring.radius,
    requiredGapPx,
    minTop: room.minTop,
    maxBottom: room.maxBottom,
    minX: room.minX,
    maxX: room.maxX,
    obstacles: room.obstacles,
  });
  if (fit.length !== naturalBoxes.length) return;
  const natural = new Map(naturalBoxes.map((b) => [b.id, b]));
  const target = fit.map(({ id: p, dx, dy }) => {
    const b = natural.get(p);
    return { p, box: { left: b.left + dx, right: b.right + dx, top: b.top + dy, bottom: b.bottom + dy } };
  });
  const worstFit = Math.max(...target.map(({ p, box }) => pillWedgeOffset(box, p.midAngle, ring.center)));
  if (!(worstFit < worstNow - wedge / 4)) return;
  for (const { p, box } of target) {
    const r = now.get(p);
    if (box.left - r.left) p.moveX(box.left - r.left);
    if (box.top - r.top) p.moveY(box.top - r.top);
  }
}

// pills: [{ id, midAngle, measureEl, transitionEl, moveY(dyPx), moveRadial(px), moveX(dxPx) }]
// in clockwise order. ring: { center, radius } of the wedges in page px (null
// skips the ring re-check and the residual pass, as before).
export function layoutRimPillStack(pills, { room, ring, requiredGapPx }) {
  const list = (pills || []).filter((p) => p && p.measureEl);
  if (!list.length) return;
  // Every pill carries a transform transition (hover grow). Suspend them for
  // the WHOLE pass — measure, stack, ring re-check AND residual shift — so no
  // move animates and every read is the real current box.
  const prev = list.map((p) => (p.transitionEl && p.transitionEl.style ? p.transitionEl.style.transition : undefined));
  list.forEach((p) => {
    if (p.transitionEl && p.transitionEl.style) p.transitionEl.style.transition = "none";
  });
  try {
    const measure = () => {
      const out = [];
      for (const p of list) {
        const r = p.measureEl.getBoundingClientRect();
        if (!(r.width > 0 && r.height > 0)) continue;
        out.push({ p, id: p.id, midAngle: p.midAngle, left: r.left, right: r.right, top: r.top, bottom: r.bottom });
      }
      return out;
    };
    let rects = measure();
    if (!rects.length) return;
    const naturalBoxes = rects.map(({ p, midAngle, left, right, top, bottom }) => ({ id: p, midAngle, left, right, top, bottom }));

    // stgfix-10191600-t22438: a very dense wheel in the Builder (61 segments,
    // Gordon 2026-10-19) gets two tidy clockwise columns instead of the sum of
    // the passes below (denseRimPillColumns.js header).
    if (ring && ring.center && isDenseRimPillCrowd(rects.length, room)) {
      const cols = resolveDenseRimPillColumns(
        rects.map(({ p, midAngle, left, right, top, bottom }) => ({ id: p, midAngle, left, right, top, bottom })),
        {
          center: ring.center,
          radius: ring.radius,
          requiredGapPx,
          minTop: room.minTop,
          maxBottom: room.maxBottom,
          minX: room.minX,
          maxX: room.maxX,
          obstacles: room.obstacles,
        }
      );
      for (const { id: p, dx, dy } of cols) {
        if (dx) p.moveX(dx);
        if (dy) p.moveY(dy);
      }
      if (cols.length) return;
    }

    // T22.295: a crowd first fans out ALONG THE RIM so the titles keep a
    // circular shape (resolveCircularPillFan header); the vertical stack
    // below only handles what the circle has no room for.
    if (ring && ring.center && hasPillOverlap(rects)) {
      const fan = resolveCircularPillFan(
        rects.map(({ p, midAngle, left, right, top, bottom }) => ({ id: p, midAngle, left, right, top, bottom })),
        { center: ring.center, radius: ring.radius, requiredGapPx, room }
      );
      for (const { id: p, dx, dy } of fan) {
        p.moveX(dx);
        p.moveY(dy);
        runRingSafetyCorrectionLoop({
          measureEl: p.measureEl,
          transitionEl: p.transitionEl,
          wedgeCenter: ring.center,
          wedgeRadius: ring.radius,
          requiredGapPx,
          onCorrect: (c) => p.moveRadial(c),
        });
      }
      if (fan.length) {
        const now = measure().map((r) => ({ ...r, id: r.p }));
        for (const { id: p, dy } of keepPolePillsOutermost(now)) p.moveY(dy);
        rects = measure();
      }
    }

    const placed = new Map(
      resolveTightPillStack(rects, {
        minTop: room.minTop,
        maxBottom: room.maxBottom,
        // stgfix-10120932-rimpills-symmetry: two pills competing for 12/6
        // o'clock sit side by side, mirrored about the wheel's axis.
        axisX: ring && ring.center ? ring.center.x : undefined,
      }).map((x) => [x.p, x])
    );
    for (const { p } of rects) {
      const s = placed.get(p);
      if (!s || (!s.dy && !s.dx)) continue;
      if (s.dx) p.moveX(s.dx);
      if (s.dy) p.moveY(s.dy);
      // Ring safety ("titles never overlap the wheel") outranks Pill Rule A.
      if (ring) {
        runRingSafetyCorrectionLoop({
          measureEl: p.measureEl,
          transitionEl: p.transitionEl,
          wedgeCenter: ring.center,
          wedgeRadius: ring.radius,
          requiredGapPx,
          onCorrect: (c) => p.moveRadial(c),
        });
      }
    }

    // audit-10101940-box-editwheel-1: ring safety can push a stacked pill back
    // onto its neighbour — clear what is left with a minimal X shift away from
    // the wheel's vertical axis (resolveResidualPillOverlaps header).
    if (ring) {
      const measureResidual = () => {
        const residual = [];
        for (const { p } of rects) {
          const r = p.measureEl.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) residual.push({ id: p, left: r.left, right: r.right, top: r.top, bottom: r.bottom });
        }
        return residual;
      };
      const shifts = resolveResidualPillOverlaps(measureResidual(), {
        axisX: ring.center.x,
        minX: room.minX,
        maxX: room.maxX,
      });
      for (const { id: p, dx } of shifts) p.moveX(dx);

      // stgfix-10130814-T22131 (Kelly T22.131, MEASURED web-stg main.608439c0,
      // /builder/507106 @1440x900, 34 segments): the two two-line pills at
      // 12 o'clock (5.3deg / 15.9deg) stay fully stacked, 133x27px. The pole
      // stack wanted to lift the outer one ~40px but the room's top (the
      // canvas clip, 72px) scaled that lift away, and the capped X pass above
      // refuses a shift wider than 0.75 of the pill (133 of ~150px). Only when
      // an overlap is LEFT (so a clean layout is never touched): shift by the
      // full overlap (inside minX/maxX, never under a canvas control), then
      // re-run the room-bounded stack on the new boxes for whatever the shift
      // could not clear — side by side now leaves the stack the room it needs.
      if (hasPillOverlap(measureResidual())) {
        const fallback = resolveResidualPillOverlaps(measureResidual(), {
          axisX: ring.center.x,
          minX: room.minX,
          maxX: room.maxX,
          maxShiftFraction: Infinity,
          obstacles: room.obstacles,
        });
        for (const { id: p, dx } of fallback) p.moveX(dx);
        const now = [];
        for (const { p } of rects) {
          const r = p.measureEl.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) now.push({ p, id: p.id, midAngle: p.midAngle, left: r.left, right: r.right, top: r.top, bottom: r.bottom });
        }
        if (hasPillOverlap(now)) {
          for (const s of resolveTightPillStack(now, {
            minTop: room.minTop,
            maxBottom: room.maxBottom,
            axisX: ring.center.x,
          })) {
            if (!s.dy && !s.dx) continue;
            if (s.dx) s.p.moveX(s.dx);
            if (s.dy) s.p.moveY(s.dy);
            runRingSafetyCorrectionLoop({
              measureEl: s.p.measureEl,
              transitionEl: s.p.transitionEl,
              wedgeCenter: ring.center,
              wedgeRadius: ring.radius,
              requiredGapPx,
              onCorrect: (c) => s.p.moveRadial(c),
            });
          }
        }
      }

      // review-1008-Frontend-7018: at narrower canvases (MEASURED live
      // /builder/507106 @1280x800 and 1024x768) the side shift above either
      // trades one overlap for another or is refused by the Undo/Redo
      // cluster, and the room-bounded re-stack has no room above. Whatever is
      // still overlapping (or sitting under a canvas control) slides down the
      // rim instead — resolveRoomlessPillCascade's header.
      const obstacles = room.obstacles || [];
      const left = measureResidual();
      const underControl = left.some((b) =>
        obstacles.some((o) => o && b.left < o.right && b.right > o.left && b.top < o.bottom && b.bottom > o.top)
      );
      if (hasPillOverlap(left) || underControl) {
        const cascade = resolveRoomlessPillCascade(
          left.map((b) => ({ ...b, midAngle: b.id.midAngle })),
          {
            axisX: ring.center.x,
            minTop: room.minTop,
            maxBottom: room.maxBottom,
            minX: room.minX,
            maxX: room.maxX,
            obstacles,
            ring,
            requiredGapPx,
          }
        );
        for (const { id: p, dx, dy } of cascade) {
          if (dx) p.moveX(dx);
          if (dy) p.moveY(dy);
        }
      }

      // T22.278 (Gordon rule 2026-10-14): whatever the passes above left, a
      // crowd follows the CLOCKWISE TANGENT — the next pill clockwise sits
      // slightly further along the circle, never straight under the previous
      // one (resolveClockwiseTangentOrder header).
      const tangent = resolveClockwiseTangentOrder(
        rects.map(({ p }) => {
          const r = p.measureEl.getBoundingClientRect();
          return { id: p, midAngle: p.midAngle, left: r.left, right: r.right, top: r.top, bottom: r.bottom };
        }),
        {
          center: ring.center,
          radius: ring.radius,
          requiredGapPx,
          minX: room.minX,
          maxX: room.maxX,
          obstacles: room.obstacles,
        }
      );
      for (const { id: p, dx } of tangent) p.moveX(dx);

      applySideColumnFallback(rects, naturalBoxes, { room, ring, requiredGapPx });
    }
  } finally {
    // Commit the final boxes with transitions still off, then restore.
    list.forEach((p) => p.measureEl.getBoundingClientRect());
    list.forEach((p, i) => {
      if (p.transitionEl && p.transitionEl.style) p.transitionEl.style.transition = prev[i];
    });
  }
}
